import { useEffect, useState } from 'react';
import { trackVisibleSections } from '../../measurement/sectionTracking';
import './EcommerceAuditLanding.css';

const EcommerceAuditLanding = () => {
  const [status, setStatus] = useState('idle');
  const [hasStartedForm, setHasStartedForm] = useState(false);

  const scrollToForm = () => {
    window.isProgrammaticScroll = true;

    const formElement = document.getElementById('auditoria-contact-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }

    window.clearTimeout(window.programmaticScrollTimeout);
    window.programmaticScrollTimeout = window.setTimeout(() => {
      window.isProgrammaticScroll = false;
    }, 1500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.target);
    const data = {
      form_id: 'auditoria-ecommerce',
      subject: 'Nueva solicitud - Auditoría y Captación de clientes - DataDos',
      name: formData.get('name'),
      company: formData.get('company'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      ecommerce_url: formData.get('ecommerce_url'),
      message: formData.get('message'),
      hp: formData.get('b_name'),
      website: 'datados.es/auditoria-ecommerce'
    };

    try {
      const response = await fetch('https://datados.es/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (result.ok) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'generate_lead',
          element_name: 'enviar_solicitud',
          element_category: 'cta',
          page_subsection2: 'formulario_contacto'
        });

        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const handleFormInteraction = () => {
    if (hasStartedForm) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'form_start' });
    setHasStartedForm(true);
  };

  useEffect(() => {
    return trackVisibleSections({
      rootSelector: '.ecommerce-audit-page',
      subsection1: 'auditoria_ecommerce'
    });
  }, []);

  useEffect(() => {
    if (status !== 'success') {
      return;
    }

    setHasStartedForm(false);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'section_view',
      section: 'landing',
      subsection_1: 'auditoria_ecommerce',
      subsection_2: 'solicitud_enviada',
      full_hierarchy: 'landing:auditoria_ecommerce:solicitud_enviada'
    });
  }, [status]);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');

    document.title = 'DataDos | Auditoría de ventas y Captación de clientes en 7 días';
    description?.setAttribute(
      'content',
      'En 7 días te digo exactamente dónde estás perdiendo dinero en tu captación de clientes. Auditoría inicial por 300€ y llamada de 15 minutos sin compromiso.'
    );

    const items = document.querySelectorAll('.ea-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    items.forEach((item) => observer.observe(item));

    return () => {
      document.title = previousTitle;
      if (previousDescription) {
        description?.setAttribute('content', previousDescription);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <div className="ecommerce-audit-page">
      <main>
        <section className="ea-hero" data-section="hero">
          <div className="ea-container ea-hero-grid">
            <div className="ea-reveal">
              <p className="ea-badge">Auditoría de ventas y captación de clientes</p>
              <h1>Estás pagando publicidad que no te trae clientes</h1>
              <p className="ea-hero-question">
                ¿Sabes qué anuncios, campañas o perfiles realmente te generan clientes?
              </p>
              <p className="ea-hero-subtitle">
                En 7 días te digo dónde se te está yendo el dinero en tu captación online: web, anuncios, Instagram o campañas.
              </p>
              <button className="ea-cta" type="button" onClick={scrollToForm}>
                Reserva una llamada de 15 min
                <span aria-hidden="true">→</span>
              </button>
              <p className="ea-trust">
                Gratuita, sin compromiso. Más de 8 años ayudando a empresas a ganar más dinero con sus datos.
              </p>
            </div>

            <div className="ea-reveal">
              <div className="ea-funnel-card" aria-label="Embudo de visitas, carrito y compra">
                <div className="ea-funnel-head">
                  <div>
                    <p>Tu recorrido de venta</p>
                    <strong>La fuga aparece antes de la compra</strong>
                  </div>
                  <span>Dinero fuera</span>
                </div>
                <div className="ea-funnel">
                  <div className="ea-funnel-stage">Visitas</div>
                  <div className="ea-funnel-stage">Interés</div>
                  <div className="ea-funnel-stage">Compra</div>
                  <div className="ea-leak" aria-hidden="true">
                    <span className="ea-leak-line" />
                    <span>fuga</span>
                    <span className="ea-leak-dot" />
                  </div>
                </div>
                <div className="ea-funnel-notes">
                  <div><strong>Entrada</strong><span>quién llega</span></div>
                  <div><strong>Interés</strong><span>quién avanza</span></div>
                  <div><strong>Venta</strong><span>quién paga</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ea-section ea-section-muted" data-section="problema">
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading">
              <h2>Inviertes en publicidad pero no sabes qué funciona</h2>
              <p>Cada día que pasa sin saberlo, estás dejando dinero sobre la mesa.</p>
            </div>
            <div className="ea-card-grid">
              <article className="ea-reveal ea-card">
                <span className="ea-icon">€</span>
                <h3>Pagas anuncios cada mes sin saber qué canal realmente genera ventas</h3>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">🛒</span>
                <h3>Ves visitas pero no entiendes por qué no llegan a convertirse en leads o clientes</h3>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">◎</span>
                <h3>Tomas decisiones a ciegas porque los datos están ahí pero nadie te los traduce</h3>
              </article>
            </div>
          </div>
        </section>

        <section className="ea-section" data-section="solucion">
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>En 7 días te entrego un diagnóstico claro y sin tecnicismos</h2>
              <p>
                Si usas Google Analytics (GA4), Google Ads o Meta Ads, los tengo en cuenta para mirar ventas,
                gasto y recorrido hasta la compra.
              </p>
            </div>
            <div className="ea-steps">
              <article className="ea-reveal ea-step">
                <span className="ea-step-number">1</span>
                <h3>Analizo tu publicidad</h3>
                <p>Reviso tus campañas, tus fuentes de visitas y lo que hacen las personas en tu web.</p>
              </article>
              <article className="ea-reveal ea-step">
                <span className="ea-step-number">2</span>
                <h3>Construyo tu funnel</h3>
                <p>Visita / Interés / Contacto / compra. Veo exactamente dónde se cae la gente.</p>
              </article>
              <article className="ea-reveal ea-step">
                <span className="ea-step-number">3</span>
                <h3>Te entrego 3 acciones</h3>
                <p>No teoría. Acciones concretas que puedes aplicar ya para ganar más dinero.</p>
              </article>
            </div>
            <p className="ea-reveal ea-section-close">
              Sin informes de 100 páginas. Sin palabrería técnica. Claridad y dinero.
            </p>
          </div>
        </section>

        <section className="ea-section ea-section-muted">
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>Esto es lo que te llevas en 7 días</h2>
            </div>
            <div className="ea-card-grid">
              <article className="ea-reveal ea-card">
                <span className="ea-icon">€</span>
                <h3>Qué anuncios sí te generan clientes</h3>
                <p>Separo lo que funciona de lo que no.</p>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">↓</span>
                <h3>Qué anuncios no funcionan</h3>
                <p>Identifico las campañas y canales que solo queman presupuesto.</p>
              </article>
              <article className="ea-reveal ea-card">
                <span className="ea-icon">✓</span>
                <h3>Qué hacer esta misma semana</h3>
                <p>3 acciones priorizadas y explicadas en lenguaje claro.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="ea-section" data-section="filtro_cliente">
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>¿Es esta auditoría para ti?</h2>
            </div>
            <div className="ea-fit-grid">
              <div className="ea-reveal ea-fit-card ea-fit-yes">
                <h3>Sí, si...</h3>
                <ul>
                  <li>Vendes online o captas clientes por Instagram, web o anuncios</li>
                  <li>Inviertes en anuncios</li>
                  <li>Tienes visitas pero no sabes por qué no convierten</li>
                  <li>Quieres decisiones claras, no informes técnicos</li>
                </ul>
              </div>
              <div className="ea-reveal ea-fit-card ea-fit-no">
                <h3>No, si...</h3>
                <ul>
                  <li>Aún no tienes ventas</li>
                  <li>No tienes un perfil en redes sociales o una web activa</li>
                  <li>Buscas solo diseño bonito sin mirar números</li>
                </ul>
              </div>
            </div>
            <p className="ea-reveal ea-honesty-note">
              Si después de la llamada inicial veo que no puedo aportarte oportunidades claras de mejora,
              te lo diré directamente y no te venderé la auditoría.
            </p>
          </div>
        </section>

        <section className="ea-section ea-section-muted" data-section="oferta_precio">
          <div className="ea-container">
            <div className="ea-reveal ea-section-heading ea-centered ea-price-heading">
              <h2>Empieza por entender dónde se te está yendo el dinero</h2>
            </div>
            <div className="ea-reveal ea-price-card">
              <div className="ea-price-main">
                <p>Auditoría completa en 7 días</p>
                <h3>300€</h3>
                <span>
                  Precio habitual: 500€. Plazas limitadas.
                </span>
              </div>
              <div className="ea-price-includes">
                <h4>Qué incluye</h4>
                <ul>
                  <li>Análisis de tu tráfico y comportamiento web</li>
                  <li>Identificación de dónde se te está yendo el dinero</li>
                  <li>Qué canales te generan ventas y cuáles solo consumen presupuesto</li>
                  <li>3 acciones claras que puedes aplicar esta misma semana</li>
                </ul>
                <strong>No necesitas saber de datos. Yo te lo explico en claro.</strong>
              </div>
              <button className="ea-cta ea-price-cta" type="button" onClick={scrollToForm}>
                Quiero saber dónde pierdo dinero
                <span aria-hidden="true">→</span>
              </button>
            </div>
            <div className="ea-reveal ea-price-secondary">
              <h3>Si necesitas ir más allá…</h3>
              <p>
                Algunos clientes, después de la auditoría inicial, deciden profundizar en análisis multicanal,
                cruce de datos entre campañas, estrategia completa de medición o dashboards personalizados.
                Eso lo vemos juntos según tu caso, sin compromiso.
              </p>
            </div>
          </div>
        </section>

        <section className="ea-section" data-section="autor_autoridad">
          <div className="ea-container ea-narrow ea-centered">
            <div className="ea-reveal">
              <p className="ea-kicker">Sobre mí</p>
              <h2>Julio Humberto Márquez, DataDos</h2>
              <p className="ea-about">
                He trabajado en proyectos de datos para entornos como CaixaBank, Vodafone, BBVA, Grupo PRISA y Telefónica.
                Ahora ayudo a negocios como el tuyo a ganar más dinero con lo que ya tienen.
              </p>
            </div>
          </div>
        </section>

        <section className="ea-final-cta">
          <div className="ea-container ea-narrow ea-centered">
            <div className="ea-reveal">
              <h2>No pierdas un día más sin saberlo</h2>
              <p>Reserva una llamada de 15 minutos. Sin compromiso.</p>
              <div className="ea-final-actions">
                <button className="ea-cta ea-cta-light" type="button" onClick={scrollToForm}>
                  Quiero saber dónde pierdo dinero
                  <span aria-hidden="true">→</span>
                </button>
                <a className="ea-cta-outline" href="https://wa.me/34626852662">
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="auditoria-contact-form" className="ea-section ea-contact-section" data-section="formulario_contacto">
          <div className="ea-container ea-narrow">
            <div className="ea-reveal ea-section-heading ea-centered">
              <h2>Cuéntame tu caso en 1 minuto</h2>
              <p>Te respondo personalmente para ver si puedo ayudarte con la auditoría.</p>
            </div>

            <div className="ea-reveal ea-form-card">
              {status === 'success' ? (
                <div className="ea-form-message ea-form-message-success" data-section="solicitud_enviada">
                  <h3>Solicitud enviada correctamente</h3>
                  <p>He recibido tu caso. Te responderé personalmente lo antes posible.</p>
                  <button className="ea-text-button" type="button" onClick={() => setStatus('idle')}>
                    Enviar otra solicitud
                  </button>
                </div>
              ) : (
                <form id="form-auditoria-ecommerce" onSubmit={handleSubmit} onFocusCapture={handleFormInteraction}>
                  <input type="text" name="b_name" className="ea-honeypot" tabIndex="-1" autoComplete="off" />

                  <div className="ea-form-grid">
                    <label>
                      <span>Nombre completo</span>
                      <input type="text" name="name" required />
                    </label>
                    <label>
                      <span>Empresa / tienda online</span>
                      <input type="text" name="company" required />
                    </label>
                  </div>

                  <div className="ea-form-grid">
                    <label>
                      <span>Email</span>
                      <input type="email" name="email" required />
                    </label>
                    <label>
                      <span>Teléfono</span>
                      <input type="tel" name="phone" />
                    </label>
                  </div>

                  <label>
                    <span>Web o perfil (Instagram, Facebook, etc.)</span>
                    <input type="url" name="ecommerce_url" placeholder="https://... o @instagram" />
                  </label>

                  <label>
                    <span>¿Dónde crees que estás perdiendo dinero?</span>
                    <textarea name="message" rows="5" />
                  </label>

                  {status === 'error' && (
                    <div className="ea-form-error">
                      No se pudo enviar el formulario. Inténtalo de nuevo o escríbeme a info@datados.es.
                    </div>
                  )}

                  <button className="ea-cta ea-form-submit" type="submit" disabled={status === 'loading'}>
                    {status === 'loading' ? 'Enviando solicitud...' : 'Enviar solicitud'}
                  </button>

                  <p className="ea-form-legal">
                    Al enviar este formulario, aceptas que DataDos use estos datos únicamente para responder a tu solicitud.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="ea-footer">
        <div className="ea-container ea-footer-content">
          <div>
            <strong>DataDos</strong>
            <p>info@datados.es · admin@datados.es · Teléfono: +34 626 852 662</p>
            <a href="https://datados.es">datados.es</a>
          </div>
          <div className="ea-footer-links">
            <a href="/aviso-legal">Aviso legal</a>
            <a href="/politica-privacidad">Política de privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EcommerceAuditLanding;
